import { getVariaveisContrato } from '../../utils/EmailAutomaticoUtils';
import { TinyMCEAction, TinyMCEConfig } from './TinyMCE';

const plugins: string[] = [
  'advlist anchor autolink',
  'charmap code',
  'fullscreen',
  'image insertdatetime',
  'link lists',
  'media',
  'pagebreak paste preview print',
  'searchreplace',
  'table',
  'visualblocks visualchars',
  'wordcount',
  'contextmenu'
];

const getToolbar = (actions: TinyMCEAction[]): string => {
  const layoutActions = [
    actions.some(action => action.name === 'inserirLayoutDuasColunas')
      ? 'inserirLayoutDuasColunas'
      : '',
    actions.some(action => action.name === 'adicionarColunaTabela')
      ? 'adicionarColunaTabela'
      : ''
  ]
    .filter(Boolean)
    .join(' ');

  return [
    'undo redo', // desfazer/refazer
    'formatselect', // blocos de texto
    'bold italic underline strikethrough link forecolor backcolor removeformat', // formatação/coloração do texto
    'alignleft aligncenter alignright alignjustify outdent indent pagebreak', // alinhamento do texto
    'bullist numlist', // listas
    ['image media file table', layoutActions].filter(Boolean).join(' | '), // mídias e layouts opcionais
    'fullscreen preview exportpdf copyContent', // ações
    'inserirVariavel' // variáveis de contrato
  ].join(' | ');
};

export const getTinyMceConfig = (actions: TinyMCEAction[]): TinyMCEConfig => {
  return {
    branding: false,
    content_style:
      'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
    height: 300,
    language: 'pt_BR',
    language_url: `${process.env.PUBLIC_URL}/langs/pt_BR.js`,
    placeholder: 'Escreva algo...',
    plugins,
    preview_styles: 'color: red',
    setup: function (editor) {
      // lógica de botões customizados
      editor.ui.registry.addButton('copyContent', {
        icon: 'copy',
        tooltip: 'Copiar Conteúdo',
        onAction: async () => {
          const html = editor.getContent();
          const plainText = editor.getContent({ format: 'text' });

          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' })
            })
          ]);
        }
      });

      if (actions.length > 0) {
        const exportPdfAction = actions.find(a => a.name === 'exportpdf');
        const inserirVariavel = actions.find(a => a.name === 'inserirVariavel');
        const inserirLayoutDuasColunas = actions.find(
          a => a.name === 'inserirLayoutDuasColunas'
        );
        const adicionarColunaTabela = actions.find(
          a => a.name === 'adicionarColunaTabela'
        );

        if (exportPdfAction) {
          editor.ui.registry.addButton('exportpdf', {
            icon: 'print',
            tooltip: 'Exportar PDF',
            onAction: () => exportPdfAction?.execute?.(editor.getContent())
          });
        }

        if (inserirVariavel) {
          editor.ui.registry.addMenuButton('inserirVariavel', {
            text: 'Inserir Variável',
            fetch: function (callback) {
              const items = [
                {
                  type: 'nestedmenuitem',
                  text: 'Variáveis de Contrato',
                  getSubmenuItems: function () {
                    return getVariaveisContrato(editor);
                  }
                }
              ];
              callback(items as any);
            }
          });
        }

        if (inserirLayoutDuasColunas?.template) {
          editor.ui.registry.addIcon(
            'fontAwesomeColumns',
            '<svg viewBox="0 0 512 512"><path d="M464 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zM224 416H64V160h160v256zm224 0H288V160h160v256z"/></svg>'
          );
          editor.ui.registry.addButton('inserirLayoutDuasColunas', {
            icon: 'fontAwesomeColumns',
            text: 'Layout em 2 colunas',
            tooltip: 'Inserir duas colunas ocupando toda a largura',
            onAction: () =>
              editor.insertContent(inserirLayoutDuasColunas.template || '')
          });
        }

        if (adicionarColunaTabela) {
          editor.ui.registry.addButton('adicionarColunaTabela', {
            icon: 'table-insert-column-after',
            text: 'Adicionar Separador',
            tooltip: 'Adicionar uma coluna à direita',
            onAction: () => {
              const selectedCell = editor.dom.getParent(
                editor.selection.getNode(),
                'td,th'
              ) as HTMLTableCellElement | null;

              if (!selectedCell) {
                return;
              }

              const table = editor.dom.getParent(
                selectedCell,
                'table'
              ) as HTMLTableElement | null;

              if (!table) {
                return;
              }

              editor.execCommand('mceTableInsertColAfter');

              const rows = Array.from(table.rows);
              const columnCount = rows.reduce(
                (largestCount, row) =>
                  Math.max(
                    largestCount,
                    Array.from(row.cells).reduce(
                      (count, cell) => count + cell.colSpan,
                      0
                    )
                  ),
                0
              );

              if (!columnCount) {
                return;
              }

              editor.dom.setStyle(table, 'width', '100%');
              rows.forEach(row =>
                Array.from(row.cells).forEach(cell =>
                  editor.dom.setStyle(
                    cell,
                    'width',
                    `${(cell.colSpan / columnCount) * 100}%`
                  )
                )
              );
            }
          });
        }
      }

      editor.on('drop', e => {
        if (e.dataTransfer) {
          const html = e.dataTransfer.getData('text/html');
          const content = html.replace(/<meta.*?>/, '');
          editor.selection.placeCaretAt(e.clientX, e.clientY);
          editor.insertContent(content);
          e.preventDefault();
        }
      });
    },
    skin: 'oxide',
    toolbar: getToolbar(actions),
    forced_root_block: false,
    spellchecker_language: 'pt_BR',
    spellchecker_languages: 'Portuguese=pt_BR',
    browser_spellcheck: true,
    contextmenu: false
  };
};
